// composables/useEnvironmentFlags.ts
import { computed } from "vue";

type Flag = "Y" | "N" | string | undefined;

const flagEnabled = (value: Flag) => value === "Y";

export function useEnvironmentFlags() {
  // env snapshot (Vite envs are static at build time)
  const env = import.meta.env;

  const apiKeyAdminEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_API_KEY_ADMIN_ENABLED));

  const dataTransferEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_TRANSFER_DATA_ENABLED));

  const managersNavEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_MANAGERS_ENABLED));

  const dataRequestsEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_DATA_REQUESTS_ENABLED));

  const aiReviewNowEnabled = computed(() => flagEnabled(env.VITE_APP_AI_REVIEW_NOW_ENABLED));

  const regionalLeadersEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_REGIONAL_LEADERS));

  const changeReviewEnabled = computed(() => flagEnabled(env.VITE_APP_NAV_REVIEW_ENABLED));

  const taggingEnabled = computed(() => flagEnabled(env.VITE_APP_RESUME_TAGGING_ENABLED));

  const versionsEnabled = computed(() => flagEnabled(env.VITE_APP_RESUME_VERSIONS_ENABLED));

  const importsEnabled = computed(() => flagEnabled(env.VITE_APP_RESUME_IMPORTS_ENABLED));

  const notificationsEnabled = computed(() => flagEnabled(env.VITE_APP_NOTIFICATIONS_ENABLED));

  const flagAdministrationEnabled = computed(() =>
    flagEnabled(env.VITE_APP_FLAG_ADMINISTRATION_ENABLED)
  );

  const advancedSearchExportEnabled = computed(() =>
    flagEnabled(env.VITE_APP_ADVANCED_SEARCH_EXPORT_ENABLED)
  );

  const flagChangeEnabled = computed(() =>
    flagEnabled(env.VITE_APP_CHANGE_LOG_FLAG_CHANGE_ENABLED)
  );

  const issuesEnabled = computed(() => flagEnabled(env.VITE_APP_ISSUES_ENABLED));

  const skillAdministrationEnabled = computed(() =>
    flagEnabled(env.VITE_APP_SKILL_ADMINISTRATION_ENABLED)
  );

  const newSkillsModalEnabled = computed(() => flagEnabled(env.VITE_APP_NEW_SKILLS_MODAL_ENABLED));
  const newVerticalsModalEnabled = computed(() =>
    flagEnabled(env.VITE_APP_NEW_VERTICALS_MODAL_ENABLED)
  );

  const skillsLimit = computed(() => Number(env.VITE_APP_SKILLS_LIMIT));
  const resumeBuilderEmail = computed(() => env.VITE_APP_RESUMEBUILDER_EMAIL);

  const isProdEnvironment = computed(
    () => (env.VITE_APP_ENVIRONMENT || "").toLowerCase() === "prod"
  );

  const deployEnv = computed(() => (env.VITE_APP_ENVIRONMENT || "").toLowerCase());
  const isProdDeploy = computed(() => deployEnv.value === "prod");

  // const isProdEnvironment = computed(() => import.meta.env.PROD);

  const useMsal = computed(() => (env.VITE_APP_AUTH_MODE || "").toLowerCase() === "msal");

  const isEnabled = (key: keyof ImportMetaEnv) => computed(() => flagEnabled(env[key]));

  const apiUrl = computed(() => env.VITE_APP_API_URL);

  const appUrl = computed(() => env.VITE_APP_RESUMEBUILDER_BASE_URL);

  return {
    // navigation
    apiKeyAdminEnabled,
    dataTransferEnabled,
    managersNavEnabled,
    regionalLeadersEnabled,
    changeReviewEnabled,

    // permissions / features
    dataRequestsEnabled,
    aiReviewNowEnabled,
    taggingEnabled,
    versionsEnabled,
    advancedSearchExportEnabled,
    flagChangeEnabled,
    issuesEnabled,
    importsEnabled,
    notificationsEnabled,
    flagAdministrationEnabled,
    skillAdministrationEnabled,
    newSkillsModalEnabled,
    newVerticalsModalEnabled,

    // environment
    isProdEnvironment,
    isProdDeploy,
    useMsal,

    skillsLimit,
    resumeBuilderEmail,
    apiUrl,
    appUrl,

    isEnabled,
  };
}
